/**
 * String transformation utilities for converting between different case formats
 * and handling object property name conversions.
 */
const DEFAULT_SEPARATOR = "-";
/**
 * Sanitizes and converts a string to a URL-friendly slug
 * @param {String} str - The string to slugify
 * @param {String} separator - Character to separate words
 * @returns {String} The slugified string
 * @throws {TypeError} If str is not a string
 */
export const slugify = (str, separator = DEFAULT_SEPARATOR) => {
    if (typeof str !== "string") {
        throw new TypeError("Input must be a string");
    }
    const sanitizedStr = str
        .trim()
        .toLowerCase()
        .replace(/[^a-zA-Z0-9_.-]+/g, separator);
    const segments = sanitizedStr.split(separator).filter(Boolean);
    const result = segments.join(separator);
    return result === separator ? "" : result;
};
/**
 * Converts a string to a slug using underscores
 * @param {string} str - The string to slugify
 * @returns {string} The slugified string
 */
export const slugifyWithUnderscore = (str) => {
    try {
        return slugify(str, "_");
    }
    catch {
        return "";
    }
};
/**
 * Splits a camelCase string into words and converts to snake_case
 * @param {string} str - The string to convert
 * @returns {string} The snake case string
 */
export const convertToSnakeCase = (str) => {
    try {
        const words = str.match(/([a-zA-Z][^A-Z]*)/g) || [str];
        return slugifyWithUnderscore(words.join(" "));
    }
    catch {
        return "";
    }
};
/**
 * Type guard to check if value is a plain object
 * @param {any} value - Value to check
 * @returns {boolean}
 */
const isPlainObject = (value) => {
    return value !== null && typeof value === "object" && !Array.isArray(value);
};
/**
 * Converts object property names using the provided conversion function
 * @param {Function} conversionFn - Property name conversion function
 * @param {Object|Array} obj - Object to convert
 * @returns {Object|Array} New object with converted property names
 */
export const convertObjectProps = (conversionFn, obj) => {
    if (typeof conversionFn !== "function" || !isPlainObject(obj)) {
        return Array.isArray(obj) ? [...obj] : {};
    }
    return Object.entries(obj).reduce((acc, [key, value]) => ({
        ...acc,
        [conversionFn(key)]: value,
    }), {});
};
/**
 * Converts object property names to snake_case
 * @param {Object} obj - Object to convert
 * @returns {Object} New object with snake_case property names
 */
export const convertObjectPropsToSnakeCase = (obj = {}) => convertObjectProps(convertToSnakeCase, obj);
